import { PALETTE } from '../../../theme/palette';

/**
 * The one categorical ramp for every chart in the Control Center.
 *
 * BarChart used to hardcode a 20-colour Tableau ramp unrelated to the brand and
 * unmeasured on dark, while Analytics used the new tokens — two chart systems
 * that disagreed. Both now read this.
 *
 * Values are resolved from the CSS custom properties at call time rather than
 * copied as literals: --indigo, --red and the tints all moved during the
 * contrast pass, and a literal would have silently drifted away from the
 * palette it is supposed to belong to.
 */

/** Ramp order. Adjacent entries are distinguishable by hue, not just lightness. */
export const CHART_TOKENS = [
  '--acc',
  '--teal',
  '--indigo',
  '--green',
  '--amber',
  '--gold',
  '--red',
  '--grey',
] as const;

/**
 * Used only when the document is unavailable (SSR, tests).
 *
 * Read from the palette rather than written here, so it cannot become the one chart colour that
 * drifts. It is deliberately the DARK value: a document that cannot be read cannot have told us
 * its theme, and dark is what the stylesheets fall back to for the same reason.
 */
const FALLBACK = PALETTE.dark['--acc'];

function readToken(name: string): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') return FALLBACK;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || FALLBACK;
}

/**
 * Resolve the ramp to concrete colours. MUI X Charts applies these as SVG
 * fills and derives its own highlight/fade states from them, so it needs real
 * values rather than `var(...)` references.
 *
 * @param count how many series need a colour; the ramp cycles if there are more.
 */
export function getChartColors(count?: number): string[] {
  const resolved = CHART_TOKENS.map(readToken);
  if (count === undefined) return resolved;
  return Array.from({ length: count }, (_, i) => resolved[i % resolved.length]);
}

/** A single named colour from the ramp — for charts that set `color` per series. */
export function chartColor(token: (typeof CHART_TOKENS)[number]): string {
  return readToken(token);
}
